import Tippy from "@tippyjs/react";
import React, { useContext, useEffect, useRef, useState } from "react";
import { ButtonsRight, SideBarOptions } from "../../../interfaces/UI";
import BtnRight from "./BtnRight";
import { useParams } from "react-router-dom";
import { useUi } from "../../../hooks";
import { PublicationContext } from "../../../context/PublicationContext";
import { typeLibro } from "../../../interfaces/Publication";
import { useHighlight } from "../../../context";
import { isDesktop } from "react-device-detect";
import { useNotification } from "../../../context/notification/notificationContext";
import { Notification } from "../RightMenu/NotificationPortal";
import ReactDOM from "react-dom";

interface ExtendedDocument extends Document {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void>;
  msFullscreenElement?: Element | null;
  msExitFullscreen?: () => Promise<void>;
}

interface ExtendedHTMLElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void>;
  msRequestFullscreen?: () => Promise<void>;
}

interface RightSectionProps {
  viewButtons: ButtonsRight;
  openButtons: ButtonsRight;
  setOpenButtons: (state: ButtonsRight) => void;
}

const MenuRightSection = ({
  viewButtons,
  openButtons,
  setOpenButtons,
}: RightSectionProps) => {
  const { idLibro, idRevista, idCapitulo } = useParams();
  const typeLibro: typeLibro = idLibro ? "libro" : "revista";
  const {
    isOpenSideBar,
    optionSidebarOpen,
    handleToggleSidebar,
    handleCloseSidebar,
  } = useUi();
  const [fullScreen, setFulScreen] = useState(false);
  const [isLight, setIsLight] = useState(false);
  const { publication } = useContext(PublicationContext);
  const { highlights } = useHighlight();
  const hasHighlights = highlights.length > 0;
  const { showHelpNotification, notificationTarget, triggerNotification, hideNotification } = useNotification();
  const helpButtonRef = useRef<HTMLButtonElement>(null);
  const notesButtonRef = useRef<HTMLButtonElement>(null);
  const [notificationPosition, setNotificationPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [notesNotificationPosition, setNotesNotificationPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const hasPodcast =
    publication?.sections?.some((section) => !!section.podcastURL) ?? false;

  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    setIsIOS(/iPad|iPhone|iPod/.test(navigator.userAgent));
  }, []);

  // SOLUCIÓN: Solo aplicar lógica de scroll en desktop
  const handleScroll = () => {
    // Si no es desktop, no hacer nada
    if (!isDesktop) return;

    const content = document.getElementById("mainContent");
    if (!content) return;

    const scrollPosition = content.scrollTop;
    const scrollThreshold = 100;
    setIsLight(scrollPosition > scrollThreshold);
  };

  useEffect(() => {
    // Si no es desktop, no agregar el event listener
    if (!isDesktop) return;

    const content = document.getElementById("mainContent");
    if (!content) return;

    content.addEventListener("scroll", handleScroll);

    return () => {
      content.removeEventListener("scroll", handleScroll);
    };
  }, [isDesktop]); // Dependencia de isDesktop

  const doFullScreen = () => {
    const element = document.documentElement as ExtendedHTMLElement;
    const doc = document as ExtendedDocument;

    if (
      doc.fullscreenElement ||
      doc.webkitFullscreenElement ||
      doc.msFullscreenElement
    ) {
      if (doc.exitFullscreen) {
        doc.exitFullscreen();
      } else if (doc.webkitExitFullscreen) {
        doc.webkitExitFullscreen();
      } else if (doc.msExitFullscreen) {
        doc.msExitFullscreen();
      }
      setFulScreen(false);
    } else {
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      }
      setFulScreen(true);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const doc = document as ExtendedDocument;
      const isFullscreen = !!(
        doc.fullscreenElement ||
        doc.webkitFullscreenElement ||
        doc.msFullscreenElement
      );
      setFulScreen(isFullscreen);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener(
      "webkitfullscreenchange",
      handleFullscreenChange as EventListener
    );
    document.addEventListener(
      "msfullscreenchange",
      handleFullscreenChange as EventListener
    );

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange as EventListener
      );
      document.removeEventListener(
        "msfullscreenchange",
        handleFullscreenChange as EventListener
      );
    };
  }, []);

  const doFullScreenIOS = () => {
    const element = document.documentElement as ExtendedHTMLElement;

    if (element.webkitRequestFullscreen) {
      element.webkitRequestFullscreen();
    } else {
      alert(
        "Para pantalla completa en iPhone: usa el modo horizontal automático"
      );
    }
  };

  const handleFullScreen = () => {
    if (isIOS) {
      doFullScreenIOS();
    } else {
      doFullScreen();
    }
  };

  const clickButton = (newState: ButtonsRight) => {
    const defaultState: ButtonsRight = {
      optConfig: false,
      optIndex: false,
      optNotes: false,
      optFileDoc: false,
      optReviews: false,
      optHelp: false,
    };
    setOpenButtons({ ...defaultState, ...newState });
    handleCloseSidebar();
  };

  // SOLUCIÓN: En móvil, siempre usar bg-sb (isLight no afecta)
  // En desktop, aplicar la lógica normal
  const getSubgroupClass = () => {
    if (isDesktop) {
      return isLight ? "" : "bg-sb";
    }
    return "bg-sb"; // En móvil, siempre bg-sb
  };

  const subgroupClass = getSubgroupClass();

  // Efecto para la notificación de ayuda
  useEffect(() => {
    if (
      showHelpNotification &&
      notificationTarget === "help-button" &&
      helpButtonRef.current &&
      viewButtons.optHelp
    ) {
      const rect = helpButtonRef.current.getBoundingClientRect();

      // Calcular posición a la izquierda del botón
      setNotificationPosition({
        x: rect.left - 270,
        y: rect.top + rect.height / 2,
      });
    } else {
      setNotificationPosition(null);
    }
  }, [showHelpNotification, notificationTarget, viewButtons.optHelp]);

  // Efecto para detectar cuando hay exactamente 1 highlight y mostrar notificación
  useEffect(() => {
    // Verificar si ya se mostró la notificación de notas
    const shownInStorage = localStorage.getItem('notesNotificationShown');
    const alreadyShown = shownInStorage === 'true';
    
    // Condición: hay exactamente 1 highlight y no se ha mostrado antes
    if (highlights.length === 1 && !alreadyShown) {
      console.log('🎯 1 highlight detectado, activando notificación de notas...');
      
      // Pequeño delay para asegurar que el botón esté renderizado
      setTimeout(() => {
        triggerNotification('notes-button');
        localStorage.setItem('notesNotificationShown', 'true');
      }, 1000);
    }
  }, [highlights.length, triggerNotification]);

  // Efecto para calcular posición de la notificación de notas
  useEffect(() => {
    if (
      showHelpNotification &&
      notificationTarget === "notes-button" &&
      notesButtonRef.current &&
      hasHighlights
    ) {
      const rect = notesButtonRef.current.getBoundingClientRect();

      // Calcular posición a la izquierda del botón
      setNotesNotificationPosition({
        x: rect.left - 270,
        y: rect.top + rect.height / 2,
      });
    } else {
      setNotesNotificationPosition(null);
    }
  }, [showHelpNotification, notificationTarget, hasHighlights]);

  return (
    <div
      className={`gui__group gui__group--right ${isOpenSideBar ? "show" : ""}`}
    >

      <div
        className={`gui__subgroup gui__subgroup--rc ${subgroupClass}`}
      >
        <div className="BtnFullScreen">
          <BtnRight
          hint={isIOS ? "Pantalla completa (iOS)" : "Pantalla completa"}
          className={`tourBtnFullScreen ${fullScreen ? "focus" : ""}`}
          help={
            isIOS
              ? "En iPhone, usa el modo horizontal o añade a pantalla de inicio"
              : "Este botón activará el modo pantalla completa"
          }
          onClick={handleFullScreen}
          icon={"icon-vpro-enter-fullscreen"}
        ></BtnRight>
        </div>

        <div className="BtnCustomize">
          {viewButtons.optConfig ? (
          <BtnRight
            hint={"Personalizar"}
            className={`tourBtnConfig ${openButtons.optConfig ? "focus" : ""}`}
            help={
              "Acá encontrarás tu información de usuario y opciones de personalización"
            }
            onClick={() => {
              clickButton({ optConfig: !openButtons.optConfig });
              handleCloseSidebar();
            }}
            icon={"icon-cog"}
          ></BtnRight>
        ) : null}
        </div>
      </div>

      <div className="gui__subgroup-container">
        <div
          id="gb-right"
          className={`gui__subgroup gui__subgroup--rc ${subgroupClass}`}
        >
          {viewButtons.optFileDoc ? (
            <BtnRight
              hint={"Ficha técnica"}
              className={`tourBtnFileDoc ${
                openButtons.optFileDoc ? "focus" : ""
              }`}
              help={"Ficha técnica del libro"}
              onClick={() => {
                clickButton({ optFileDoc: !openButtons.optFileDoc });
                handleCloseSidebar();
              }}
              icon={"icon-vpro-sec-tech-sheet"}
            ></BtnRight>
          ) : null}

          {viewButtons.optIndex ? (
            <BtnRight
              hint={`Indice del ${
                typeLibro === "libro" ? "capítulo" : "Artículos"
              }`}
              className={`tourBtnIndex ${openButtons.optIndex ? "focus" : ""}`}
              help={`Indice del ${
                typeLibro === "libro" ? "capítulo" : "Artículos"
              }`}
              onClick={() => {
                clickButton({ optIndex: !openButtons.optIndex });
                handleCloseSidebar();
              }}
              icon={"icon-vpro-index"}
            ></BtnRight>
          ) : null}

          {hasHighlights && (
            <div style={{ position: "relative" }}>
              <BtnRight
                ref={notesButtonRef}
                hint={"Notas"}
                className={`tourBtnNotes ${openButtons.optNotes ? "focus" : ""}`}
                help={"Textos resaltados y notas"}
                onClick={() => {
                  clickButton({ optNotes: !openButtons.optNotes });
                  handleCloseSidebar();
                }}
                icon={"icon-vpro-comments"}
                data-button-id="notes-button"
              />

              {/* NOTIFICACIÓN PARA NOTAS RENDERIZADA CON PORTAL */}
              {showHelpNotification &&
                notificationTarget === "notes-button" &&
                notesNotificationPosition &&
                hasHighlights &&
                ReactDOM.createPortal(
                  <Notification
                    message="Haz clic aquí para ver todas tus notas y resaltados"
                    position={notesNotificationPosition}
                    onClose={() => {
                      hideNotification();
                    }}
                    duration={6000}
                  />,
                  document.body
                )}
            </div>
          )}

          {viewButtons.optPodcast && hasPodcast ? (
            <BtnRight
              hint={"Podcast"}
              className={`tourPodcastBtn ${
                openButtons.optPodcast ? "focus" : ""
              }`}
              help={"Podcast"}
              onClick={() => {
                clickButton({ optPodcast: !openButtons.optPodcast });
                handleCloseSidebar();
              }}
              icon={"icon-podcast"}
            ></BtnRight>
          ) : null}

          {publication.asistenteKey && (
            <div className="BtnAI">
              <BtnRight
                hint={"Asistente IA"}
                className={`tourBtnIA ${openButtons.optIA ? "focus" : ""}`}
                help={"Asistente IA"}
                onClick={() => {
                  clickButton({ optIA: !openButtons.optIA });
                  handleCloseSidebar();
                }}
                icon={"icon-ai-assistant"}
              ></BtnRight>
            </div>
          )}
        </div>

        <div className={`gui__subgroup gui__subgroup--rc ${subgroupClass}`}>
          {viewButtons.optReviews ? (
            <BtnRight
              hint={"Reviews"}
              className={`tourBtnReviews ${
                openButtons.optReviews ? "focus" : ""
              }`}
              help={"Reseñas del Libro"}
              onClick={() => {
                clickButton({ optReviews: !openButtons.optReviews });
                handleCloseSidebar();
              }}
              icon={"icon-sec-rating"}
            ></BtnRight>
          ) : null}

          <div className="BtnReviews">
            <BtnRight
              hint={"Reseñas"}
              className={`tourBtnReviews ${openButtons.optReviews ? "focus" : ""}`}
              help={"Reseñas"}
              onClick={() => {
                clickButton({ optReviews: !openButtons.optReviews });
                handleCloseSidebar();
              }}
              icon={"icon-sec-rating"}
            ></BtnRight>
          </div>

          {viewButtons.optHelp ? (
            <div style={{ position: "relative" }}>
              <BtnRight
                ref={helpButtonRef}
                hint={"Ayuda"}
                className={`tourBtnHelp ${openButtons.optHelp ? "focus" : ""}`}
                help={"Ayuda"}
                onClick={() => {
                  clickButton({ optHelp: !openButtons.optHelp });
                  handleCloseSidebar();
                }}
                icon={"icon-vpro-fc-help"}
                data-button-id="help-button"
              />

              {/* NOTIFICACIÓN RENDERIZADA CON PORTAL */}
              {showHelpNotification &&
                notificationTarget === "help-button" &&
                notificationPosition &&
                viewButtons.optHelp &&
                ReactDOM.createPortal(
                  <Notification
                    message="No olvides calificar este recurso haciendo clic acá"
                    position={notificationPosition}
                    onClose={() => {
                      hideNotification();
                    }}
                    duration={6000}
                  />,
                  document.body
                )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default MenuRightSection;